import {useEffect} from "react"
import {createCanvasFromVideo} from "../utils/createCanvasFromVideoUtils"
import {
    calculateImageDimensions,
    calculateCenterPosition,
} from "../utils/calculationsUtils"
import type {CropperHookReturn} from "../types/types"

interface UseVideoToggleProps {
    cropper: CropperHookReturn
}

export const useVideoToggle = ({cropper}: UseVideoToggleProps) => {
    const toggleVideo = () => {
        if (!cropper || !cropper.videoElement) {
            return
        }

        const video = cropper.videoElement
        const isVideoPlaying = cropper.imageState.videoPlaying

        if (isVideoPlaying) {
            video.play()
            cropper.setImageState({
                videoPlaying: true,
            })
        } else {
            video.pause()

            const {stillImage} = createCanvasFromVideo(video)

            stillImage.onload = () => {
                const dimensions = calculateImageDimensions(
                    stillImage,
                    cropper.cropState.dimensions
                )
                const position = calculateCenterPosition(dimensions)

                cropper.setImageState({
                    status: "loaded",
                    dimensions,
                    position,
                    zoom: cropper.imageState.zoom,
                    videoPlaying: false,
                })

                cropper.setCurrentImageUrl(stillImage.src)

                const zoomFactor = cropper.imageState.zoom / 100
                cropper.setRenderData({
                    position,
                    dimensions: {
                        width: dimensions.width * zoomFactor,
                        height: dimensions.height * zoomFactor,
                    },
                })
            }
        }
    }

    useEffect(() => {
        if (cropper?.videoElement && cropper.imageState.status === "loaded") {
            toggleVideo()
        }
    }, [cropper?.imageState.videoPlaying])

    return {toggleVideo}
}
